import React, { useEffect, useRef, useState } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { Building2, Plus, Search, Edit, Trash2, X, Mail, Phone, MapPin, UserPlus, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useToast } from '../../contexts/ToastContext';
import centresService from '../../services/centresService';
import AddCentreDialog from '../../components/AddCentreDialog/AddCentreDialog';
import EditCentreDialog from '../../components/EditCentreDialog/EditCentreDialog';
import AssignResponsablesToCentreDialog from '../../components/AssignResponsablesToCentreDialog/AssignResponsablesToCentreDialog';
import ViewCentreDialog from '../../components/ViewCentreDialog/ViewCentreDialog';
import './CentresPage.css';

const CentresPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const isMobile = useIsMobile();
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedCentre, setSelectedCentre] = useState(null);

  const fetchCentres = async () => {
    try {
      setLoading(true);
      const params = {};
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;

      const response = await centresService.getAll(params);
      const data = Array.isArray(response) ? response : response?.results || response?.centres || [];
      setCentres(data);
    } catch (error) {
      console.error('Error fetching centres:', error);
      setCentres([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCentres();
  }, [debouncedSearchTerm]);

  const handleCentreAdded = () => {
    fetchCentres();
  };

  const handleCentreUpdated = () => {
    fetchCentres();
  };

  const openEditDialog = (centre) => {
    setSelectedCentre(centre);
    setIsEditDialogOpen(true);
  };

  const openAssignDialog = (centre) => {
    setSelectedCentre(centre);
    setIsAssignDialogOpen(true);
  };

  const openViewDialog = (centre) => {
    setSelectedCentre(centre);
    setIsViewDialogOpen(true);
  };

  const handleDelete = async (item) => {
    const toastId = toast.custom(
      'danger',
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
          {t('centres.confirmDeleteTitle', { defaultValue: 'Supprimer ce centre ?' })}
        </div>
        <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          <strong>{item?.name}</strong><br/>
          {t('centres.confirmDeleteMessage', { defaultValue: 'Cette action est irréversible.' })}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="toast-btn toast-btn-danger"
            onClick={async () => {
              toast.remove(toastId);
              try {
                await centresService.delete(item.id);
                setCentres((prev) => prev.filter((c) => c.id !== item.id));
                toast.success(t('centres.deleteSuccess', { defaultValue: 'Centre supprimé.' }));
              } catch (error) {
                console.error('Error deleting centre:', error);
                toast.error(t('centres.deleteError'));
              }
            }}
          >
            {t('common.delete', { defaultValue: 'Supprimer' })}
          </button>
          <button
            type="button"
            className="toast-btn"
            onClick={() => toast.remove(toastId)}
          >
            {t('common.cancel', { defaultValue: 'Annuler' })}
          </button>
        </div>
      </div>,
      { duration: null }
    );
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 800);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      setDebouncedSearchTerm(searchTerm);
    }
  };

  const handleSearchBlur = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchTerm !== debouncedSearchTerm) {
      setDebouncedSearchTerm(searchTerm);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('centres.loading')}</span>
      </div>
    );
  }

  return (
    <div className="centres-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <Building2 size={32} />
            {t('centres.title')}
          </h1>
          <p className="page-subtitle">{t('centres.subtitle')}</p>
        </div>

        {canCreate('centres') && (
          <button onClick={() => setIsAddDialogOpen(true)} className="btn-add">
            <Plus size={20} />
            {t('centres.add')}
          </button>
        )}
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={t('centres.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={handleClearSearch}
              title={t('common.clearSearch')}
              type="button"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="ui-fixed-list">
        <div className="ui-fixed-list-scroll">
          <div className="table-container">
            <table className={`data-table${isMobile ? ' data-table-mobile' : ''}`}>
              <thead>
                <tr>
                  {isMobile ? (
                    <>
                      <th>{t('centres.table.name')}</th>
                      <th className="actions-column">{t('centres.table.actions')}</th>
                    </>
                  ) : (
                    <>
                      <th>{t('centres.table.name')}</th>
                      <th>{t('centres.table.contact')}</th>
                      <th>{t('centres.table.phone')}</th>
                      <th>{t('centres.table.address')}</th>
                      <th className="actions-column">{t('centres.table.actions')}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {centres.length === 0 ? (
                  <tr>
                    <td colSpan={isMobile ? 2 : 5} className="empty-state">
                      <Building2 size={48} className="empty-icon" />
                      <h3>{t('centres.empty.title')}</h3>
                      <p>{t('centres.empty.text')}</p>
                    </td>
                  </tr>
                ) : (
                  centres.map((item) => (
                    isMobile ? (
                      <tr key={item.id} className="centres-mobile-row">
                        <td className="centres-mobile-name">{item.name}</td>
                        <td className="centres-mobile-actions">
                          <div className="centres-actions-wrapper">
                            <button className="action-btn action-view" onClick={() => openViewDialog(item)} title={t('common.view', { defaultValue: 'Voir' })} type="button"><Eye size={16} /></button>
                            <button className="action-btn action-assign" onClick={() => openAssignDialog(item)} title={t('centres.actions.assignResponsables')} type="button"><UserPlus size={16} /></button>
                            {canEdit('centres') && (
                              <button className="action-btn action-edit" onClick={() => openEditDialog(item)} title={t('common.edit')} type="button"><Edit size={16} /></button>
                            )}
                            {canDelete('centres') && (
                              <button className="action-btn action-delete" onClick={() => handleDelete(item)} title={t('centres.actions.delete')} type="button"><Trash2 size={16} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.id}>
                        <td className="name-cell">
                          <div className="centre-name">
                            <Building2 size={16} />
                            {item.name}
                            {item.is_active === false && <span className="centre-inactive">({t('status.inactive')})</span>}
                          </div>
                        </td>
                        <td className="email-cell">
                          {item.email ? (
                            <a href={`mailto:${item.email}`} className="email-link">
                              <Mail size={14} />
                              {item.email}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="phone-cell">
                          {item.phone ? (
                            <a href={`tel:${item.phone}`} className="phone-link">
                              <Phone size={14} />
                              {item.phone}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="address-cell">
                          <span className="address-text">
                            {item.address ? (
                              <>
                                <MapPin size={14} />
                                {item.address.substring(0, 40)}
                                {item.address.length > 40 ? '...' : ''}
                              </>
                            ) : (
                              '-'
                            )}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button className="action-btn action-view" onClick={() => openViewDialog(item)} title={t('common.view', { defaultValue: 'Voir' })} type="button"><Eye size={16} /></button>
                          <button className="action-btn action-assign" onClick={() => openAssignDialog(item)} title={t('centres.actions.assignResponsables')} type="button"><UserPlus size={16} /></button>
                          {canEdit('centres') && (
                            <button className="action-btn action-edit" onClick={() => openEditDialog(item)} title={t('common.edit')} type="button"><Edit size={16} /></button>
                          )}
                          {canDelete('centres') && (
                            <button className="action-btn action-delete" onClick={() => handleDelete(item)} title={t('centres.actions.delete')} type="button"><Trash2 size={16} /></button>
                          )}
                        </td>
                      </tr>
                    )
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddCentreDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onCentreAdded={handleCentreAdded}
      />

      <EditCentreDialog
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedCentre(null);
        }}
        centre={selectedCentre}
        onCentreUpdated={handleCentreUpdated}
      />

      <AssignResponsablesToCentreDialog
        isOpen={isAssignDialogOpen}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setSelectedCentre(null);
        }}
        centre={selectedCentre}
        onUpdated={fetchCentres}
      />

      <ViewCentreDialog
        isOpen={isViewDialogOpen}
        onClose={() => {
          setIsViewDialogOpen(false);
          setSelectedCentre(null);
        }}
        centre={selectedCentre}
      />
    </div>
  );
};

export default CentresPage;
